// ============================================================================
// PROMPT 24 — User Globalization Preference Engine & Store
// Guarantees: Language ≠ Locale ≠ Market ≠ Jurisdiction ≠ Currency
// ============================================================================

import { UserGlobalPreferences, TextDirection } from './globalizationTypes';
import { LOCALES_REGISTRY, MARKETS_REGISTRY } from './globalizationRegistry';
import { BidirectionalUIEngine } from './localizationEngines';

const STORAGE_KEY = 'yamoopay_global_preferences_v1';

class UserPreferenceEngine {
  private currentPreferences: UserGlobalPreferences;
  private listeners: ((prefs: UserGlobalPreferences) => void)[] = [];

  constructor() {
    this.currentPreferences = this.loadFromStorage();
  }

  private loadFromStorage(): UserGlobalPreferences {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // ignore
    }

    return {
      preferredLanguage: 'en',
      preferredLocale: 'en-US',
      preferredCurrency: 'USD',
      selectedMarket: 'US',
      taxJurisdiction: 'JUR-US-FED',
      billingCountry: 'US',
      textDirection: 'LTR',
      pseudolocalizationEnabled: false,
      sourceOrigin: 'DEFAULT'
    };
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.currentPreferences));
    } catch {
      // ignore
    }
    this.notify();
  }

  private notify(): void {
    this.listeners.forEach((fn) => fn(this.currentPreferences));
  }

  public getPreferences(): UserGlobalPreferences {
    return { ...this.currentPreferences };
  }

  public subscribe(listener: (prefs: UserGlobalPreferences) => void): () => void {
    this.listeners.push(listener);
    listener(this.currentPreferences);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * CRITICAL GUARANTEE:
   * Changing Language strictly changes UI text strings and direction.
   * It DOES NOT mutate tax jurisdiction, statutory rules, or billing country!
   */
  public setLanguage(languageCode: string): void {
    const direction: TextDirection = BidirectionalUIEngine.getTextDirection(languageCode);
    
    // Find matching locale or keep current
    let matchedLocale = this.currentPreferences.preferredLocale;
    const localeDef = LOCALES_REGISTRY.find((l) => l.languageCode === languageCode);
    if (localeDef) {
      matchedLocale = localeDef.bcp47;
    }

    this.currentPreferences = {
      ...this.currentPreferences,
      preferredLanguage: languageCode,
      preferredLocale: matchedLocale,
      textDirection: direction,
      sourceOrigin: 'USER_SELECTED'
    };

    this.save();
  }

  public setLocale(bcp47: string): void {
    const localeDef = LOCALES_REGISTRY.find((l) => l.bcp47.toLowerCase() === bcp47.toLowerCase());
    const lang = localeDef ? localeDef.languageCode : this.currentPreferences.preferredLanguage;
    const direction = localeDef ? localeDef.textDirection : BidirectionalUIEngine.getTextDirection(lang);

    this.currentPreferences = {
      ...this.currentPreferences,
      preferredLocale: bcp47,
      preferredLanguage: lang,
      textDirection: direction,
      sourceOrigin: 'USER_SELECTED'
    };

    this.save();
  }

  /**
   * Changing Display Currency DOES NOT mutate tax jurisdiction.
   */
  public setCurrency(currencyCode: string): void {
    this.currentPreferences = {
      ...this.currentPreferences,
      preferredCurrency: currencyCode.toUpperCase(),
      sourceOrigin: 'USER_SELECTED'
    };

    this.save();
  }

  /**
   * Changing Market allows user to adopt market defaults or keep individual overrides.
   */
  public setMarket(marketCode: string, adoptDefaults = false): void {
    const market = MARKETS_REGISTRY.find((m) => m.marketCode === marketCode);
    if (!market) return;

    if (adoptDefaults) {
      this.currentPreferences = {
        ...this.currentPreferences,
        selectedMarket: market.marketCode,
        taxJurisdiction: market.jurisdictionPackId,
        billingCountry: market.countryCode,
        preferredCurrency: market.defaultCurrency,
        preferredLocale: market.defaultLocale,
        sourceOrigin: 'USER_SELECTED'
      };
    } else {
      this.currentPreferences = {
        ...this.currentPreferences,
        selectedMarket: market.marketCode,
        taxJurisdiction: market.jurisdictionPackId,
        sourceOrigin: 'USER_SELECTED'
      };
    }

    this.save();
  }

  public setTaxJurisdiction(jurisdictionId: string): void {
    this.currentPreferences = {
      ...this.currentPreferences,
      taxJurisdiction: jurisdictionId,
      sourceOrigin: 'USER_SELECTED'
    };

    this.save();
  }

  public togglePseudolocalization(): void {
    this.currentPreferences = {
      ...this.currentPreferences,
      pseudolocalizationEnabled: !this.currentPreferences.pseudolocalizationEnabled
    };

    this.save();
  }
}

export const globalUserPreferences = new UserPreferenceEngine();
